/** Codigos de error definidos en CONTRACT.md. */
export type ApiErrorCode =
  | 'VALIDATION_ERROR'
  | 'UNAUTHORIZED'
  | 'FORBIDDEN'
  | 'NOT_FOUND'
  | 'GONE'
  | 'RATE_LIMITED'
  | 'CONFLICT'
  | 'PLAN_LIMIT'
  | 'PAYLOAD_TOO_LARGE'
  | 'NOT_CONFIGURED'
  | 'INTERNAL'
  | 'NETWORK';

export interface ApiErrorDetail {
  path?: string;
  message?: string;
}

/**
 * Error de la API con el mensaje ya en español, listo para mostrarlo en un
 * toast sin traducir nada en la capa de UI.
 */
export class ApiError extends Error {
  readonly code: ApiErrorCode;
  readonly status: number;
  readonly details: ApiErrorDetail[];

  constructor(
    code: ApiErrorCode,
    message: string,
    status = 0,
    details: ApiErrorDetail[] = [],
  ) {
    super(message);
    this.name = 'ApiError';
    this.code = code;
    this.status = status;
    this.details = details;
  }
}

const FALLBACK_MESSAGES: Record<ApiErrorCode, string> = {
  VALIDATION_ERROR: 'Hay datos que no son válidos. Revisa el formulario.',
  UNAUTHORIZED: 'Tu sesión ha caducado. Vuelve a iniciar sesión.',
  FORBIDDEN: 'No tienes permisos para hacer esto.',
  GONE: 'El enlace o borrador ha caducado.',
  RATE_LIMITED: 'Has agotado la cuota. Vuelve a intentarlo cuando se renueve.',
  NOT_FOUND: 'No hemos encontrado lo que buscabas.',
  CONFLICT: 'Ese registro ya existe o ha cambiado desde que lo abriste.',
  PLAN_LIMIT: 'Has alcanzado el límite de tu plan. Mejora el plan para continuar.',
  PAYLOAD_TOO_LARGE: 'La grabación es demasiado grande. Hazla un poco más corta.',
  NOT_CONFIGURED: 'Esta función todavía no está configurada.',
  INTERNAL: 'Se ha producido un error inesperado. Inténtalo de nuevo en un momento.',
  NETWORK: 'No hemos podido conectar con el servidor. Comprueba tu conexión.',
};

export function fallbackMessage(code: ApiErrorCode): string {
  return FALLBACK_MESSAGES[code] ?? FALLBACK_MESSAGES.INTERNAL;
}

/** Mensaje presentable para el usuario, venga de donde venga el error. */
export function errorMessage(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  if (error instanceof Error && error.message) return error.message;
  return FALLBACK_MESSAGES.INTERNAL;
}
